import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import DevBanner from './components/layout/DevBanner';
import LoginPage from './components/auth/LoginPage';
import AuthCallback from './components/auth/AuthCallback';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PageLoading from './components/common/PageLoading';
import FloatingVoiceFeedback from './components/feedback/FloatingVoiceFeedback';
import { useBranding } from './contexts/BrandingContext';

// "/" goes to the admin-configured landing page (Settings → General). Waits
// for the branding fetch so we don't flash Home and then jump elsewhere.
function RootRedirect() {
  const { defaultLandingPage, isLoading } = useBranding();
  if (isLoading) return <PageLoading />;
  return <Navigate to={defaultLandingPage ?? '/home'} replace />;
}

// Every page is lazy-loaded so a phone only downloads the code for the screen
// it's on (the wiki editor, Gantt chart, charts, etc. live in their own
// chunks). Layout and the auth shell stay eager — they're needed on first
// paint. The Suspense boundary that shows the spinner lives inside Layout,
// around its <Outlet/>, so the header/sidebar stay put while a page loads.
const HomePage = lazy(() => import('./pages/HomePage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const WikiLayout = lazy(() => import('./components/wiki/WikiLayout'));
const WikiHomePage = lazy(() => import('./pages/WikiHomePage'));
const WikiLinksPage = lazy(() => import('./pages/WikiLinksPage'));
const WikiPageViewRoute = lazy(() =>
  import('./pages/WikiRoutes').then((m) => ({ default: m.WikiPageViewRoute })),
);
const WikiPageEditorRoute = lazy(() =>
  import('./pages/WikiRoutes').then((m) => ({ default: m.WikiPageEditorRoute })),
);
const WikiPageHistoryRoute = lazy(() =>
  import('./pages/WikiRoutes').then((m) => ({ default: m.WikiPageHistoryRoute })),
);
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const SettingsLayout = lazy(() => import('./pages/settings/SettingsLayout'));
const SettingsTemplatePage = lazy(() => import('./pages/settings/SettingsTemplatePage'));
const SettingsUsersPage = lazy(() => import('./pages/settings/SettingsUsersPage'));
const SettingsDomainsPage = lazy(() => import('./pages/settings/SettingsDomainsPage'));
const SettingsFeedbackPage = lazy(() => import('./pages/settings/SettingsFeedbackPage'));
const SettingsBrandingPage = lazy(() => import('./pages/settings/SettingsBrandingPage'));
const SettingsIntegrationsPage = lazy(() => import('./pages/settings/SettingsIntegrationsPage'));
const SettingsRemindersPage = lazy(() => import('./pages/settings/SettingsRemindersPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const GraphicsAndSwagPage = lazy(() => import('./pages/GraphicsAndSwagPage'));
const SocialsPage = lazy(() => import('./pages/SocialsPage'));
const WebAndTechPage = lazy(() => import('./pages/WebAndTechPage'));
const SponsorsPage = lazy(() => import('./pages/SponsorsPage'));
const OperationsPage = lazy(() => import('./pages/OperationsPage'));
const AVPage = lazy(() => import('./pages/AVPage'));
const SpeakersPage = lazy(() => import('./pages/SpeakersPage'));
const BoardPage = lazy(() => import('./pages/BoardPage'));
const FilesPage = lazy(() => import('./pages/FilesPage'));
const OrganizersPage = lazy(() => import('./pages/OrganizersPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'));
const PublicSharePage = lazy(() => import('./pages/PublicSharePage'));
const RetrospectivePage = lazy(() => import('./pages/RetrospectivePage'));
const DayOfAppPage = lazy(() => import('./pages/DayOfAppPage'));
const DayOfDashboardPage = lazy(() => import('./pages/DayOfDashboardPage'));
const EmailBlastPage = lazy(() => import('./pages/EmailBlastPage'));
const CfpDashboardPage = lazy(() => import('./pages/CfpDashboardPage'));
const PublicCfpSubmitPage = lazy(() => import('./pages/PublicCfpSubmitPage'));
const PublicCfpStatusPage = lazy(() => import('./pages/PublicCfpStatusPage'));

// The public CFP lives on its own vanity host (cfw.bsidesboulder.org, with
// cfv.* as an alias to the same form; cfp.* now belongs to the talks CFP on
// Sessionize). On that
// host we serve ONLY the host-facing routes — no toolkit chrome,
// no organizer surface — and send the root to the submission form.
const IS_CFP_HOST = /^(cfw|cfv)\./.test(window.location.hostname);

function CfpHostApp() {
  return (
    <>
      {/* The CFP host bypasses the toolkit Layout, so the dev banner is rendered
          here directly — otherwise cfw.dev.bsidesboulder.org would show no
          environment warning. Self-gates on the dev hostname. */}
      <DevBanner />
      <Suspense fallback={<PageLoading />}>
        <Routes>
        {/* The vanity host is CFP-only, so the submission form is the root. The
            /cfp/* paths are kept as aliases because already-sent magic links
            (status) and invite links point at them. */}
        <Route path="/" element={<PublicCfpSubmitPage />} />
        <Route path="/cfp/submit" element={<PublicCfpSubmitPage />} />
        <Route path="/status/:token" element={<PublicCfpStatusPage />} />
        <Route path="/cfp/status/:token" element={<PublicCfpStatusPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

// On the main host the standalone CFP pages (operator console + public
// submit/status) bypass the toolkit Layout, so they'd otherwise show no
// dev-environment warning on dev.organize.bsidesboulder.org. Wrap them so the
// banner sits above the page. The vanity cfp.* host gets its banner from
// CfpHostApp instead, so these routes never double up.
function CfpStandalone({ children }: { children: React.ReactNode }) {
  return (
    <>
      <DevBanner />
      {children}
    </>
  );
}

function App() {
  if (IS_CFP_HOST) return <CfpHostApp />;
  return (
    <>
      {/* Mounted once at the app root (outside <Layout/>) so it overlays both the
          Layout-wrapped toolkit routes AND the standalone /cfp console, and keeps
          its recording state across route changes between them. The widget
          self-gates on isAdmin && a dev hostname, so it stays hidden for
          logged-out users and on the login screen. Not rendered on the public
          cfp.* host (that branch returns early above). */}
      <FloatingVoiceFeedback />
      <Suspense fallback={<PageLoading />}>
        <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/share/:token" element={<PublicSharePage />} />

        {/* Public CFP (also reachable on the main host for convenience). */}
        <Route path="/cfp/submit" element={<CfpStandalone><PublicCfpSubmitPage /></CfpStandalone>} />
        <Route path="/cfp/status/:token" element={<CfpStandalone><PublicCfpStatusPage /></CfpStandalone>} />

        {/* CFP operator platform — authenticated but standalone (no toolkit
            sidebar/header), so it reads as its own focused console. */}
        <Route path="/cfp" element={<ProtectedRoute><CfpStandalone><CfpDashboardPage /></CfpStandalone></ProtectedRoute>} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<RootRedirect />} />
          <Route path="home" element={<HomePage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="tasks/:id" element={<TasksPage />} />
          <Route path="meetings" element={<MeetingsPage />} />
          <Route path="retrospective" element={<RetrospectivePage />} />
          {/* Legacy routes now live as tabs inside Retrospective */}
          <Route path="lessons-learned" element={<Navigate to="/retrospective?tab=lessons" replace />} />
          {/* Templates moved to /settings/template — redirect old URLs. */}
          <Route path="templates" element={<Navigate to="/settings/template" replace />} />
          <Route path="templates/new" element={<Navigate to="/settings/template" replace />} />
          <Route path="templates/:id" element={<Navigate to="/settings/template" replace />} />
          <Route path="wiki" element={<WikiLayout />}>
            <Route index element={<WikiHomePage />} />
            <Route path="new" element={<WikiPageEditorRoute />} />
            <Route path="files" element={<FilesPage />} />
            <Route path="links" element={<WikiLinksPage />} />
            <Route path=":slug" element={<WikiPageViewRoute />} />
            <Route path=":slug/edit" element={<WikiPageEditorRoute />} />
            <Route path=":slug/history" element={<WikiPageHistoryRoute />} />
          </Route>
          <Route path="graphics-and-swag" element={<GraphicsAndSwagPage />} />
          <Route path="socials" element={<SocialsPage />} />
          <Route path="web-and-tech" element={<WebAndTechPage />} />
          {/* Outreach folded into Sponsors; volunteers moved into Operations. */}
          <Route path="outreach" element={<Navigate to="/sponsors" replace />} />
          {/* Legacy combined Socials & Web route — redirect to standalone Socials. */}
          <Route path="socials-and-web" element={<Navigate to="/socials" replace />} />
          <Route path="sponsors" element={<SponsorsPage />} />
          <Route path="operations" element={<OperationsPage />} />
          <Route path="venue-operations" element={<Navigate to="/operations" replace />} />
          <Route path="volunteers" element={<Navigate to="/operations" replace />} />
          <Route path="av" element={<AVPage />} />
          <Route path="speakers" element={<SpeakersPage />} />
          <Route path="board" element={<BoardPage />} />
          <Route path="day-of-app" element={<DayOfAppPage />} />
          <Route path="dayof" element={<DayOfDashboardPage />} />
          <Route path="feedback" element={<Navigate to="/retrospective?tab=feedback" replace />} />
          {/* Files moved under the Wiki tabs — keep old deep links working,
              preserving the ?folder= query string via relative navigation. */}
          <Route path="files" element={<Navigate to="/wiki/files" replace />} />
          <Route path="organizers" element={<OrganizersPage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="email-blast" element={<EmailBlastPage />} />
          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<SettingsPage />} />
            <Route path="template" element={<SettingsTemplatePage />} />
            <Route path="users" element={<SettingsUsersPage />} />
            <Route path="domains" element={<SettingsDomainsPage />} />
            <Route path="branding" element={<SettingsBrandingPage />} />
            <Route path="feedback" element={<SettingsFeedbackPage />} />
            <Route path="integrations" element={<SettingsIntegrationsPage />} />
            {/* Reminders tab renamed to Notifications. Keep the old path working. */}
            <Route path="notifications" element={<SettingsRemindersPage />} />
            <Route path="reminders" element={<Navigate to="/settings/notifications" replace />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
